"use client";

import {
  Anchor,
  Box,
  Button,
  Divider,
  Flex,
  Group,
  Image,
  LoadingOverlay,
  Paper,
  Stack,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { useToggle } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import { invariant } from "es-toolkit";
import ky from "ky";
import NextImage from "next/image";
import { useRouter, useSearchParams } from "next/navigation";
import Script from "next/script";
import { useTranslations } from "next-intl";
import { usePostHog } from "posthog-js/react";
import { useEffect, useState } from "react";
import logo from "@/app/MyKaraoke Video.png";
import { AppConfig } from "@/config";
import { showErrorNotification } from "@/helpers/notifications";
import {
  ANALYTICS_EVENTS,
  type AuthMethod,
  type AuthStartedProps,
} from "@/lib/analytics/events";
import { createClient } from "@/lib/supabase";
import { AppleButton } from "./AppleButton";
import { DiscordButton } from "./DiscordButton";
import { GoogleButton } from "./GoogleButton";

import classes from "./Login.module.css";

interface LoginFormValues {
  email: string;
  name: string;
}

// Successfull login redirects to /api/auth/callback where the Code Exchange is processed (see app/api/auth/callback/route.js).
export default function Login() {
  const t = useTranslations("app.login.Login");
  const supabase = createClient();
  const posthog = usePostHog();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isDisabled, setIsDisabled] = useState<boolean>(false);

  const searchParams = useSearchParams();

  const initialToggleType = searchParams.has("via")
    ? "signup"
    : searchParams.get("t") === "signup"
      ? "signup"
      : "login";
  const [type, toggle] = useToggle<AuthStartedProps["mode"]>([
    initialToggleType,
    initialToggleType === "login" ? "signup" : "login",
  ]);

  const router = useRouter();

  useEffect(() => {
    if (searchParams.get("error")) {
      showErrorNotification({
        title: t("errors.loginFailed"),
        message: t("errors.tryDifferentAccount"),
      });

      router.push("/login");
    }
  }, [router, searchParams, t]);

  const form = useForm<LoginFormValues>({
    initialValues: {
      email: "",
      name: "",
    },

    validate: {
      email: (val) => {
        if (
          val.toLowerCase().endsWith("@gmail.com") ||
          val.toLowerCase().endsWith("@googlemail.com")
        ) {
          return t("errors.useGoogleForGmail");
        }

        if (
          val.toLowerCase().endsWith("@icloud.com") ||
          val.toLowerCase().endsWith("@privaterelay.appleid.com")
        ) {
          return t("errors.useAppleForICloud");
        }

        return /^\S+@\S+$/.test(val) ? null : t("errors.invalidEmail");
      },
      name: (val) =>
        type === "signup" && val.length < 3 && t("errors.invalidName"),
    },
  });

  const handleSignup = async (
    options: {
      type: string;
      provider?: Exclude<AuthMethod, "email">;
    },
    email?: string,
    data: Record<string, string | number> = {},
  ) => {
    setIsLoading(true);

    try {
      const { type: authType, provider } = options;

      posthog?.capture(ANALYTICS_EVENTS.authStarted, {
        method: provider ?? "email",
        mode: type,
      } satisfies AuthStartedProps);

      const promoteKitCode = window.promotekit_referral;
      const nextParam = searchParams.get("next");

      const params = new URLSearchParams();
      if (promoteKitCode) params.set("pkid", promoteKitCode);
      if (nextParam) params.set("next", nextParam);

      const redirectURL = `${window.location.origin}/api/auth/callback?${params.toString()}`;

      if (authType === "oauth" && provider) {
        console.log("Attempting sign in with OAuth", { provider, redirectURL });

        await supabase.auth.signInWithOAuth({
          provider,
          options: {
            redirectTo: redirectURL,
            scopes: "email",
          },
        });
      } else if (authType === "otp") {
        invariant(email, "Email is required");

        try {
          await ky.post("/api/auth/email", {
            json: {
              email,
              name: data?.name,
              redirectURL,
              promoteKitCode,
            },
          });
        } catch (error) {
          console.error("OTP request failed:", error);
          notifications.show({
            title: t("errors.signupFailed"),
            message: t("errors.betterEmailAddress"),
            autoClose: false,
          });
          setIsDisabled(false);
          setIsLoading(false);
          return;
        }

        setIsDisabled(false);
        setIsLoading(false);

        // Redirect to OTP page with email parameter
        const otpParams = new URLSearchParams();
        otpParams.set("email", email);

        if (data.name) {
          otpParams.set("name", data.name as string);
        }

        if (promoteKitCode) {
          otpParams.set("pkid", promoteKitCode);
        }

        router.push(`/login/otp?${otpParams.toString()}`);
      }
    } catch (error) {
      showErrorNotification({
        title: t("errors.signinupFailed"),
        message: t("errors.tryDifferentAccountEmail"),
      });
      console.log(error);
      setIsLoading(false);
    }
  };

  return (
    <>
      <Script
        src="https://cdn.promotekit.com/promotekit.js"
        data-promotekit="c52b6199-6d45-4920-bb53-bb6b515ea9ba"
      />

      <Paper className={classes.form} radius={0} p={30} pos="relative">
        <LoadingOverlay
          visible={isLoading}
          zIndex={1000}
          overlayProps={{ radius: "sm", blur: 2 }}
        />
        <Image
          src={logo}
          component={NextImage}
          alt={`${AppConfig.appName} logo`}
          priority={true}
          height={60}
          quality={100}
          style={{ height: "60px", width: "100%" }}
          fit="contain"
        />
        <Title order={3} className={classes.title} ta="center" mt="md" mb={50}>
          {type === "signup" ? t("createAccountTitle") : t("loginTitle")}
        </Title>
        <Stack mb="md" mt="md">
          <GoogleButton
            radius="xl"
            onClick={() => handleSignup({ type: "oauth", provider: "google" })}
          >
            {t("continueWithGoogle")}
          </GoogleButton>
          <AppleButton
            radius="xl"
            onClick={() => handleSignup({ type: "oauth", provider: "apple" })}
          >
            {t("continueWithApple")}
          </AppleButton>
          <DiscordButton
            radius="xl"
            onClick={() => handleSignup({ type: "oauth", provider: "discord" })}
          >
            {t("continueWithDiscord")}
          </DiscordButton>
        </Stack>
        <Divider
          label={<Text size="md">{t("orContinueWithEmail")}</Text>}
          labelPosition="center"
          my="lg"
        />
        <form
          onSubmit={form.onSubmit(async ({ email, name }) => {
            await handleSignup({ type: "otp" }, email, {
              name,
            });
          })}
        >
          <Stack>
            {type === "signup" && (
              <TextInput
                required
                label={t("nameLabel")}
                value={form.values.name}
                onChange={(event) =>
                  form.setFieldValue("name", event.currentTarget.value)
                }
                error={form.errors.name}
                radius="md"
              />
            )}

            <TextInput
              required
              label={t("emailLabel")}
              value={form.values.email}
              onChange={(event) =>
                form.setFieldValue("email", event.currentTarget.value)
              }
              error={form.errors.email}
              radius="md"
            />

            <Group justify="flex-end">
              <Button
                type="submit"
                radius="xl"
                disabled={isLoading || isDisabled}
              >
                {type === "signup" ? t("signUpButton") : t("loginButton")}
              </Button>
            </Group>
          </Stack>

          <Flex justify="center" mt="md">
            <Anchor
              component="button"
              type="button"
              c="dimmed"
              onClick={() => toggle()}
              size="sm"
            >
              {type === "signup"
                ? t("alreadyHaveAccount")
                : t("noAccountSignUp")}
            </Anchor>
          </Flex>
        </form>

        {/* Portal target for dev login buttons */}
        <Box mt={30} id="dev-login-portal" />
      </Paper>
    </>
  );
}
